"use client";

import * as React from "react";
import { motion, useAnimationFrame, useMotionValue } from "framer-motion";

export function DraggableCarousel({
  children,
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  const x = useMotionValue(0);
  const contentRef = React.useRef<HTMLDivElement>(null);
  const [isPaused, setIsPaused] = React.useState(false);

  useAnimationFrame((time, delta) => {
    if (!contentRef.current || isPaused) return;

    // We duplicated the slides, so the true width to loop at is exactly half the total scroll width
    const containerWidth = contentRef.current.scrollWidth / 2;

    // Adjust this multiplier to change the speed (0.03 is a smooth, ambient speed)
    let newX = x.get() - delta * 0.03;

    // Seamlessly loop back to start when we hit the midway point
    if (newX <= -containerWidth) {
      newX += containerWidth;
    } else if (newX > 0) {
      // In case the user forcefully drags it backwards past 0
      newX -= containerWidth;
    }

    x.set(newX);
  });

  return (
    <div
      className={`relative overflow-hidden w-full ${className || ""}`.trim()}
      {...props}
    >
      <motion.div
        ref={contentRef}
        style={{ x }}
        drag="x"
        // Infinite constraints so dragging doesn't snap back to 0
        dragConstraints={{ left: -Infinity, right: Infinity }}
        onPointerDown={() => setIsPaused(true)}
        onPointerUp={() => setIsPaused(false)}
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
        className="flex items-center w-max cursor-grab active:cursor-grabbing gap-8 pr-8"
      >
        {children}
      </motion.div>
    </div>
  );
}
